// Drop-in replacements for firebase/database's set/update/push/remove that
// make offline writes durable.
//
// The RTDB web SDK has no disk persistence: a write made while disconnected is
// held in memory and sent on reconnect — but only if the page is still open.
// Close the tab, reload, or have the phone kill a backgrounded PWA, and it's
// gone without a trace. So any write issued while disconnected is also logged
// to localStorage, and cleared once the SDK settles it. Anything still logged
// when a later page load starts (its writer died first) gets replayed.
//
// Every logged write targets a fixed path (push keys are generated client-side
// up front), so a replay just rewrites the same data — safe to repeat.
// runTransaction isn't wrapped: it needs live server state and can't be replayed.
//
// Collaboration: an offline change mustn't silently undo what someone else saved meanwhile.
// So while offline an update is sent as one transaction per field. Its first run sees this
// device's copy (the value the person was looking at, "before"); on reconnect Firebase reruns
// it against the server's value. If that's no longer "before" (someone else changed the field),
// it isn't overwritten: the change becomes a conflict in the sync chip, where the person keeps
// theirs or uses their own. A field this device never had cached (before = null) is simply sent.

import { set as sdkSet, update as sdkUpdate, push as sdkPush, remove as sdkRemove, child as sdkChild, ref, runTransaction } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database, isFirebaseConnected, onFirebaseConnectedChange } from './initFirebase';

const LOG_KEY = 'pendingDbWrites';
const OWNERS_KEY = 'pendingDbWriteOwners';
const OWNER_STALE_MS = 30000;
const STUCK_AFTER_ATTEMPTS = 5;
const CHANGE_EVENT = 'pendingdbwriteschange';

// One id per page load. Entries from an owner that stopped heart-beating are
// orphans; a live owner's entries are left alone (its SDK is still delivering
// them, and replaying alongside could land an older value after a newer one).
const OWNER = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const live = new Set();

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}

function writeJSON(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function notify() {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function pathOf(r) {
  return decodeURIComponent(new URL(r.toString()).pathname).replace(/^\/+/, '');
}

function heartbeat() {
  const owners = readJSON(OWNERS_KEY, {});
  const now = Date.now();
  if (live.size) owners[OWNER] = now; else delete owners[OWNER];
  Object.keys(owners).forEach(o => { if (owners[o] < now - 24 * 3600 * 1000) delete owners[o]; });
  try { writeJSON(OWNERS_KEY, owners); } catch {}
}
setInterval(() => { if (live.size) heartbeat(); }, OWNER_STALE_MS / 3);

// A page that closes cleanly hands its unsent entries over at once instead of
// making the next page wait out the staleness window.
window.addEventListener('pagehide', () => {
  if (!live.size) return;
  const owners = readJSON(OWNERS_KEY, {});
  delete owners[OWNER];
  try { writeJSON(OWNERS_KEY, owners); } catch {}
});

function log(op, r, value) {
  if (isFirebaseConnected()) return null;
  const entry = {
    id: `${OWNER}-${live.size}-${Math.random().toString(36).slice(2, 8)}`,
    op, path: pathOf(r), value: value === undefined ? null : value,
    savedAt: Date.now(), owner: OWNER, attempts: 0,
  };
  try {
    writeJSON(LOG_KEY, [...readJSON(LOG_KEY, []), entry]);
  } catch (err) {
    // Quota or serialization failure — the SDK still holds the write in memory.
    console.warn('[trackedDb] could not log offline write to device storage:', err);
    return null;
  }
  live.add(entry.id);
  heartbeat();
  notify();
  return entry.id;
}

function clear(id) {
  writeJSON(LOG_KEY, readJSON(LOG_KEY, []).filter(e => e.id !== id));
  if (live.delete(id)) heartbeat();
  notify();
}

// Writes this page has sent that the server hasn't answered yet — the sync chip's
// "Saving {n}". In memory only: each page (and each iframe) counts its own.
let inFlight = 0;

// A write the server refused (permission denied) is kept in the log as a stuck entry
// instead of vanishing, so the sync chip can show "{n} not saved" and offer Try again /
// Discard. Other failures (a dropped connection) are retried by the SDK or the replay.
function logRejected(op, r, value, err) {
  const entry = {
    id: `rej-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    op, path: pathOf(r), value: value === undefined ? null : value,
    savedAt: Date.now(), owner: 'rejected', attempts: 1, permanent: true,
    lastError: String(err?.code || err?.message || err),
  };
  try { writeJSON(LOG_KEY, [...readJSON(LOG_KEY, []), entry]); } catch { /* storage full — nothing more to do */ }
  notify();
}

function track(promise, id, op, r, value) {
  inFlight++;
  notify();
  const settle = (err) => {
    inFlight = Math.max(0, inFlight - 1);
    if (id) clear(id); else notify();
    if (err && isPermanent(err)) logRejected(op, r, value, err);
  };
  promise.then(() => settle(null), settle);
  return promise;
}

export function set(r, value) {
  return track(sdkSet(r, value), log('set', r, value), 'set', r, value);
}

export function update(r, values) {
  const id = log('update', r, values);
  if (!id) return track(sdkUpdate(r, values), null, 'update', r, values);
  return track(guardedUpdate(r, values, id), id, 'update', r, values);
}

// ─── Offline changes that meet someone else's ─────────────────────────────

function same(a, b) {
  if (a === b) return true;
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') return a == null && b == null;
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every(k => same(a[k], b[k]));
}

function rememberBefore(id, key, before) {
  writeJSON(LOG_KEY, readJSON(LOG_KEY, []).map(e => (e.id === id ? { ...e, befores: { ...(e.befores || {}), [key]: before } } : e)));
}

function logConflict(path, mine, theirs) {
  const entry = {
    id: `conf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    op: 'update', path, value: mine, theirs, conflict: true,
    savedAt: Date.now(), owner: 'conflict', attempts: 1, permanent: true,
    lastError: 'Changed by someone else while this device was offline',
  };
  try { writeJSON(LOG_KEY, [...readJSON(LOG_KEY, []), entry]); } catch { /* storage full */ }
  notify();
}

// One transaction per field; `befores` (from a replayed log entry) stands in for the first run.
function guardedUpdate(r, values, id, befores = null) {
  const conflicts = {};
  const runs = Object.entries(values).map(([key, value]) => {
    const mine = value === undefined ? null : value;
    let before = befores && key in befores ? befores[key] : undefined;
    return runTransaction(sdkChild(r, key), (cur) => {
      if (before === undefined) { // the first, local run: this device's copy
        before = cur;
        if (id) rememberBefore(id, key, cur);
        return mine;
      }
      // cur null: this device has no copy (the server answers with the real value and this reruns).
      if (before === null || cur === null || same(cur, before) || same(cur, mine)) return mine;
      conflicts[key] = cur;
      return undefined; // leave theirs
    });
  });
  return Promise.all(runs).then((results) => {
    const keys = Object.keys(conflicts);
    if (keys.length) {
      logConflict(pathOf(r), Object.fromEntries(keys.map(k => [k, values[k] ?? null])), conflicts);
      console.warn('[trackedDb] offline change met a newer one; kept theirs, flagged for review:', pathOf(r), keys);
    }
    return results;
  });
}

export function remove(r) {
  return track(sdkRemove(r), log('remove', r, null), 'remove', r, null);
}

/** Writes this page has sent that the server hasn't confirmed yet. */
export function inFlightWrites() {
  return inFlight;
}

// Same contract as the SDK's push(): with no value it only makes a key; with a
// value it returns a ThenableReference whose promise resolves to the new ref.
export function push(parent, value) {
  const thenable = sdkPush(parent);
  if (value === undefined) return thenable;
  const plain = sdkChild(parent, thenable.key); // not thenable — resolving to it can't loop
  const p = set(plain, value).then(() => plain);
  thenable.then = p.then.bind(p);
  thenable.catch = p.catch.bind(p);
  return thenable;
}

// ─── Replaying orphaned writes ────────────────────────────────────────────

const isStuckEntry = e => !!e.permanent || (e.attempts || 0) >= STUCK_AFTER_ATTEMPTS;
const isPermanent = err => /permission[_ ]denied/i.test(`${err?.code || ''} ${err?.message || ''}`);

function runEntry(e, { force = false } = {}) {
  const r = ref(database, e.path);
  if (e.op === 'set') return sdkSet(r, e.value);
  // A conflict being resolved with "use mine" is written as it is; a replayed offline change
  // still checks it isn't landing on someone else's newer value.
  if (e.op === 'update') return force || e.conflict || !e.befores ? sdkUpdate(r, e.value) : guardedUpdate(r, e.value, null, e.befores);
  if (e.op === 'remove') return sdkRemove(r);
  return Promise.reject(new Error(`unknown op ${e.op}`));
}

let replaying = false;

async function replayOrphans({ includeStuck = false } = {}) {
  if (replaying || !auth.currentUser || !isFirebaseConnected()) return;
  replaying = true;
  try {
    const owners = readJSON(OWNERS_KEY, {});
    const cutoff = Date.now() - OWNER_STALE_MS;
    const orphans = readJSON(LOG_KEY, [])
      .filter(e => e.owner !== OWNER && !(owners[e.owner] > cutoff))
      .filter(e => !e.conflict) // a conflict waits for the person's choice (useMinePendingWrite / discard)
      .filter(e => includeStuck || !isStuckEntry(e))
      .sort((a, b) => a.savedAt - b.savedAt);
    let synced = 0;
    for (const e of orphans) {
      try {
        await runEntry(e);
        clear(e.id);
        synced++;
      } catch (err) {
        console.warn('[trackedDb] replay failed, keeping it for next time:', e.path, err);
        writeJSON(LOG_KEY, readJSON(LOG_KEY, []).map(x => x.id === e.id
          ? { ...x, attempts: (x.attempts || 0) + 1, lastError: String(err?.message || err), permanent: isPermanent(err) }
          : x));
        notify();
      }
    }
    if (synced) window.dispatchEvent(new CustomEvent('pendingdbwritessynced', { detail: { count: synced } }));
  } finally {
    replaying = false;
  }
}

// Only the top-level page replays — the app shell's iframes share its storage,
// so they'd otherwise all race to replay the same entries.
if (window.top === window) {
  onAuthStateChanged(auth, user => { if (user) replayOrphans(); });
  onFirebaseConnectedChange(connected => { if (connected) replayOrphans(); });
  // Picks up entries whose writer died without a clean close (its heartbeat
  // goes stale while this page is already connected, so no event fires).
  setInterval(() => { if (readJSON(LOG_KEY, []).length) replayOrphans(); }, OWNER_STALE_MS);
}

// ─── For UIs that show what's waiting ─────────────────────────────────────

export function pendingWrites() {
  return readJSON(LOG_KEY, []).map(e => ({
    ...e,
    stuck: isStuckEntry(e),
  }));
}

export function discardPendingWrite(id) {
  clear(id);
}

export function retryPendingWrites() {
  return replayOrphans({ includeStuck: true });
}

/**
 * For a page's own offline queue: writes `values` unless a field has changed on the server since
 * the person saw `befores` (a field → the value they were looking at). A field that changed is
 * left as it is and becomes a conflict in the sync chip.
 */
export function updateIfUnchanged(r, values, befores) {
  return guardedUpdate(r, values, null, befores);
}

/** A conflict resolved as "use mine": this device's value is written over theirs. */
export async function useMinePendingWrite(id) {
  const e = readJSON(LOG_KEY, []).find(x => x.id === id);
  if (!e) return;
  await runEntry(e, { force: true });
  clear(id);
}

/** Calls fn whenever the log changes — in this page, or in another tab/frame. */
export function onPendingWritesChange(fn) {
  const onStorage = e => { if (e.key === LOG_KEY) fn(); };
  window.addEventListener(CHANGE_EVENT, fn);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, fn);
    window.removeEventListener('storage', onStorage);
  };
}
